import React from "react";
import { Button } from "@/components/Button";
import { IngredientSectionDrawerData } from "@/store";
import { useMediaQuery } from "@mui/material";
import theme from "@/theme";
import DrawerHeader from "@/components/DrawerHeader";
import FullScreenDrawer from "@/components/FullScreenDrawer";

export type IngredientDrawerProps = {
  open: boolean;
  onExit: () => void;
  drawerData: IngredientSectionDrawerData;
};
export const IngredientDrawer = ({
  open,
  onExit,
  drawerData,
}: IngredientDrawerProps) => {
  const fullScreen: boolean = useMediaQuery(
    theme.breakpoints.down(theme.breakpoints.values.sm)
  );
  const excludedIngredients = drawerData.excludedIngredients ?? null;
  const highlightedFeatures = drawerData.highlightedFeatures ?? null;
  const ingredients = drawerData.ingredients ?? null;
  const ingredientDisclosureLink = drawerData.ingredientDisclosureLink ?? null;

  return (
    <FullScreenDrawer
      anchor="right"
      open={open}
      fullscreen={fullScreen ? "true" : undefined}
      role="dialog"
      aria-label="formulation"
      aria-modal={true}
      onClose={onExit}
    >
      <DrawerHeader sticky={true} handleClose={onExit} text="FORMULATION" />
      <div className="w-full space-y-8 p-5">
        {excludedIngredients && excludedIngredients.ingredients.length > 0 && (
          <div className="space-y-4">
            <p className="mb-0 text-base font-bold uppercase text-black">
              {excludedIngredients.heading}
            </p>
            <div className="space-y-2 rounded-[3px] border border-solid border-red-600 bg-orange-50 p-4">
              {excludedIngredients.ingredients.map((ingredient, index) => {
                return (
                  <div
                    className="flex items-center gap-x-4 leading-[0]"
                    key={ingredient + index}
                  >
                    <div className="w-4 flex-shrink-0">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="17"
                        fill="none"
                      >
                        <path
                          style={{ fill: "hsl(var(--color-red-600))" }}
                          d="M8 16.5c4.411 0 8-3.589 8-8a7.948 7.948 0 0 0-2.342-5.658A7.948 7.948 0 0 0 8 .5c-4.411 0-8 3.589-8 8 0 2.139.832 4.148 2.342 5.658A7.948 7.948 0 0 0 8 16.5Zm0-1.333a6.62 6.62 0 0 1-4.22-1.504l9.383-9.384A6.62 6.62 0 0 1 14.667 8.5 6.674 6.674 0 0 1 8 15.167ZM8 1.833c1.558 0 3.033.53 4.22 1.504L2.837 12.72A6.62 6.62 0 0 1 1.333 8.5 6.674 6.674 0 0 1 8 1.833Z"
                        />
                      </svg>
                    </div>
                    <p className="text-base text-black">{ingredient}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {highlightedFeatures && highlightedFeatures.features.length > 0 && (
          <div className="space-y-4">
            <p className="mb-0 text-base font-bold uppercase text-black">
              {highlightedFeatures.heading}
            </p>
            <div className="space-y-2 rounded-[3px] border border-solid border-secondary-600 bg-secondary-50 p-4">
              {highlightedFeatures.features.map((feature, index) => {
                return (
                  <div
                    className="flex items-center gap-x-4 leading-[0]"
                    key={feature + index}
                  >
                    <div className="w-4 flex-shrink-0">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="17"
                        fill="none"
                      >
                        <g
                          style={{ fill: "hsl(var(--color-secondary-600))" }}
                          clipPath="url(#a)"
                        >
                          <path d="M8 16.5a8 8 0 1 1 0-16 8 8 0 0 1 0 16ZM8 1.643a6.857 6.857 0 1 0 0 13.714A6.857 6.857 0 0 0 8 1.643Z" />
                          <path d="M7.349 11.929a.571.571 0 0 1-.383-.149L4.263 9.283a.571.571 0 0 1 .771-.84l2.24 2.074 4-5.228a.573.573 0 1 1 .915.691l-4.355 5.714a.572.572 0 0 1-.405.235h-.08Z" />
                        </g>
                        <defs>
                          <clipPath id="a">
                            <path fill="#fff" d="M0 .5h16v16H0z" />
                          </clipPath>
                        </defs>
                      </svg>
                    </div>
                    <p className="text-base text-black">{feature}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {ingredients && ingredients.ingredients.length > 0 && (
          <div className="space-y-4">
            <p className="mb-0 text-base font-bold uppercase text-black">
              {ingredients.heading}
            </p>
            <div className="space-y-2">
              {ingredients.ingredients.map((ingredient, index) => {
                return (
                  <div
                    className={`flex gap-x-4 pb-2 leading-[0] ${
                      index < ingredients.ingredients.length - 1
                        ? "border-0 border-b border-solid border-b-primary-600"
                        : ""
                    }`}
                    key={ingredient.name + index}
                  >
                    <div className="basis-2/4 pr-1">
                      <p className="text-sm text-black">{ingredient.name}</p>
                    </div>
                    <div className="basis-2/4 pl-1 text-right">
                      <p className="text-sm text-black">{ingredient.type}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {ingredientDisclosureLink && (
          <Button
            variant="outline"
            className="h-[50px] w-full bg-transparent text-base uppercase"
            component="a"
            href={ingredientDisclosureLink}
            target="_blank"
          >
            INGREDIENT DISCLOSURES
          </Button>
        )}
      </div>
    </FullScreenDrawer>
  );
};
